import { useState, useMemo, useCallback } from 'react';
import { runBacktest } from '../utils/backtestEngine';

const INDICATORS = [
  { id: 'sma', name: 'SMA', params: { period: 20 }, series: 'line' },
  { id: 'ema', name: 'EMA', params: { period: 20 }, series: 'line' },
  { id: 'rsi', name: 'RSI', params: { period: 14 }, series: 'line' },
  { id: 'macd', name: 'MACD', params: {}, series: 'macd' },
  { id: 'macd', name: 'MACD Signal', params: {}, series: 'signal' },
  { id: 'bollingerBands', name: 'BB Upper', params: { period: 20, stdDev: 2 }, series: 'upper' },
  { id: 'bollingerBands', name: 'BB Lower', params: { period: 20, stdDev: 2 }, series: 'lower' },
  { id: 'atr', name: 'ATR', params: { period: 14 }, series: 'line' },
  { id: 'stochastic', name: 'Stoch %K', params: {}, series: 'k' },
  { id: 'adx', name: 'ADX', params: { period: 14 }, series: 'adx' },
  { id: 'supertrend', name: 'Supertrend', params: {}, series: 'line' },
  { id: 'cci', name: 'CCI', params: { period: 20 }, series: 'line' },
];

const OPERATORS = [
  { id: '>', label: '>' },
  { id: '<', label: '<' },
  { id: '>=', label: '>=' },
  { id: '<=', label: '<=' },
  { id: 'crossover', label: 'Crosses Above' },
  { id: 'crossunder', label: 'Crosses Below' },
];

const EMPTY_SIDE = { type: 'indicator', indicator: 'sma', params: { period: 20 }, series: 'line', source: 'close', value: '0' };

function ConditionRow({ condition, onChange, onRemove }) {
  const updateSide = (side, field, val) => {
    const updated = { ...condition, [side]: { ...condition[side], [field]: val } };
    if (field === 'type' && val === 'indicator') {
      updated[side] = { ...EMPTY_SIDE };
    }
    if (field === 'indicator') {
      const ind = INDICATORS.find(i => i.id === val && i.name.includes(condition[side].name || '')) || INDICATORS.find(i => i.id === val);
      if (ind) { updated[side].params = { ...ind.params }; updated[side].series = ind.series; }
    }
    onChange(updated);
  };

  const renderSide = (side) => {
    const s = condition[side];
    return (
      <div className="st-side">
        <select value={s.type} onChange={e => updateSide(side, 'type', e.target.value)}>
          <option value="indicator">Indicator</option>
          <option value="price">Price</option>
          <option value="number">Number</option>
        </select>
        {s.type === 'indicator' && (
          <>
            <select value={`${s.indicator}|${s.series}`} onChange={e => {
              const [ind, ser] = e.target.value.split('|');
              const meta = INDICATORS.find(i => i.id === ind && i.series === ser);
              updateSide(side, 'indicator', ind);
              if (meta) {
                const c = { ...condition[side], indicator: ind, series: ser, params: { ...meta.params } };
                onChange({ ...condition, [side]: c });
              }
            }}>
              {INDICATORS.map((ind, i) => (
                <option key={i} value={`${ind.id}|${ind.series}`}>{ind.name}</option>
              ))}
            </select>
            {Object.entries(s.params || {}).map(([k, v]) => (
              <input key={k} type="number" value={v} title={k} style={{ width: 50 }}
                onChange={e => updateSide(side, 'params', { ...s.params, [k]: parseFloat(e.target.value) || 0 })} />
            ))}
          </>
        )}
        {s.type === 'price' && (
          <select value={s.source || 'close'} onChange={e => updateSide(side, 'source', e.target.value)}>
            <option value="close">Close</option>
            <option value="open">Open</option>
            <option value="high">High</option>
            <option value="low">Low</option>
          </select>
        )}
        {s.type === 'number' && (
          <input type="number" step="any" value={s.value || ''} style={{ width: 80 }}
            onChange={e => updateSide(side, 'value', e.target.value)} />
        )}
      </div>
    );
  };

  return (
    <div className="st-condition">
      {renderSide('left')}
      <select className="st-operator" value={condition.operator} onChange={e => onChange({ ...condition, operator: e.target.value })}>
        {OPERATORS.map(op => <option key={op.id} value={op.id}>{op.label}</option>)}
      </select>
      {renderSide('right')}
      <button className="st-remove" onClick={onRemove}>✕</button>
    </div>
  );
}

function ConditionGroup({ label, conditions, onChange, color }) {
  const add = () => onChange([...conditions, {
    left: { ...EMPTY_SIDE },
    operator: 'crossover',
    right: { type: 'indicator', indicator: 'ema', params: { period: 50 }, series: 'line' },
  }]);
  const remove = (i) => onChange(conditions.filter((_, idx) => idx !== i));
  const update = (i, c) => onChange(conditions.map((cc, idx) => idx === i ? c : cc));

  return (
    <div className="st-group">
      <div className="st-group-header" style={{ borderLeft: `3px solid ${color}` }}>
        <span>{label}</span>
        <button className="st-add-btn" onClick={add}>+ Add Condition</button>
      </div>
      {conditions.map((c, i) => (
        <ConditionRow key={i} condition={c} onChange={v => update(i, v)} onRemove={() => remove(i)} />
      ))}
      {conditions.length === 0 && <div className="st-empty">No conditions (disabled)</div>}
    </div>
  );
}

export default function StrategyTester({ candles, onClose }) {
  const [entryLong, setEntryLong] = useState([{
    left: { type: 'indicator', indicator: 'sma', params: { period: 9 }, series: 'line' },
    operator: 'crossover',
    right: { type: 'indicator', indicator: 'sma', params: { period: 21 }, series: 'line' },
  }]);
  const [entryShort, setEntryShort] = useState([]);
  const [exitLong, setExitLong] = useState([{
    left: { type: 'indicator', indicator: 'sma', params: { period: 9 }, series: 'line' },
    operator: 'crossunder',
    right: { type: 'indicator', indicator: 'sma', params: { period: 21 }, series: 'line' },
  }]);
  const [exitShort, setExitShort] = useState([]);
  const [stopLoss, setStopLoss] = useState('2');
  const [takeProfit, setTakeProfit] = useState('4');
  const [trailingStop, setTrailingStop] = useState('0');
  const [logic, setLogic] = useState('AND');
  const [capital, setCapital] = useState('10000');
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  const runTest = useCallback(() => {
    if (!candles || candles.length < 100) { alert('Need at least 100 candles'); return; }
    setRunning(true);
    setTimeout(() => {
      const r = runBacktest({
        entryLong, entryShort, exitLong, exitShort,
        stopLossPct: parseFloat(stopLoss) || 0,
        takeProfitPct: parseFloat(takeProfit) || 0,
        trailingPct: parseFloat(trailingStop) || 0,
        logic, capital: parseFloat(capital) || 10000,
      }, candles);
      setResults(r);
      setRunning(false);
    }, 50);
  }, [candles, entryLong, entryShort, exitLong, exitShort, stopLoss, takeProfit, trailingStop, logic, capital]);

  return (
    <div className="strategy-tester">
      <div className="st-header">
        <h3>Strategy Tester</h3>
        <button className="st-close" onClick={onClose}>✕</button>
      </div>

      <div className="st-body">
        <div className="st-config">
          {/* Conditions */}
          <ConditionGroup label="Entry Long" conditions={entryLong} onChange={setEntryLong} color="#22c55e" />
          <ConditionGroup label="Exit Long" conditions={exitLong} onChange={setExitLong} color="#ef4444" />
          <ConditionGroup label="Entry Short" conditions={entryShort} onChange={setEntryShort} color="#ef4444" />
          <ConditionGroup label="Exit Short" conditions={exitShort} onChange={setExitShort} color="#22c55e" />

          {/* Risk Management */}
          <div className="st-risk">
            <div className="st-risk-row">
              <label>Logic</label>
              <select value={logic} onChange={e => setLogic(e.target.value)}>
                <option value="AND">AND (all conditions)</option>
                <option value="OR">OR (any condition)</option>
              </select>
            </div>
            <div className="st-risk-row">
              <label>Capital</label>
              <input type="number" value={capital} onChange={e => setCapital(e.target.value)} />
            </div>
            <div className="st-risk-row">
              <label>Stop Loss %</label>
              <input type="number" step="0.1" value={stopLoss} onChange={e => setStopLoss(e.target.value)} />
            </div>
            <div className="st-risk-row">
              <label>Take Profit %</label>
              <input type="number" step="0.1" value={takeProfit} onChange={e => setTakeProfit(e.target.value)} />
            </div>
            <div className="st-risk-row">
              <label>Trailing %</label>
              <input type="number" step="0.1" value={trailingStop} onChange={e => setTrailingStop(e.target.value)} />
            </div>
          </div>

          <button className="btn btn-primary st-run" onClick={runTest} disabled={running}>
            {running ? 'Running...' : `Run Backtest (${candles?.length || 0} bars)`}
          </button>
        </div>

        {/* Results */}
        {results && (
          <div className="st-results">
            <h4>Results</h4>
            <div className="st-stats-grid">
              <div className="st-stat"><span>Total Trades</span><b>{results.stats.totalTrades}</b></div>
              <div className="st-stat"><span>Win Rate</span><b>{results.stats.winRate}%</b></div>
              <div className="st-stat">
                <span>Net P&L</span>
                <b className={results.stats.totalPnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                  {results.stats.totalPnl >= 0 ? '+' : ''}{results.stats.totalPnl}
                </b>
              </div>
              <div className="st-stat"><span>Return</span><b className={results.stats.returnPct >= 0 ? 'pnl-positive' : 'pnl-negative'}>{results.stats.returnPct}%</b></div>
              <div className="st-stat"><span>Profit Factor</span><b>{results.stats.profitFactor}</b></div>
              <div className="st-stat"><span>Expectancy</span><b>{results.stats.expectancy}</b></div>
              <div className="st-stat"><span>Avg Win</span><b className="pnl-positive">+{results.stats.avgWin}</b></div>
              <div className="st-stat"><span>Avg Loss</span><b className="pnl-negative">-{results.stats.avgLoss}</b></div>
              <div className="st-stat"><span>Max DD</span><b className="pnl-negative">{results.stats.maxDrawdown} ({results.stats.maxDrawdownPct}%)</b></div>
              <div className="st-stat"><span>Buy & Hold</span><b>{results.stats.buyHoldReturn}%</b></div>
              <div className="st-stat"><span>Avg Holding</span><b>{results.stats.avgBars} bars</b></div>
              <div className="st-stat"><span>W/L</span><b>{results.stats.wins}/{results.stats.losses}</b></div>
            </div>

            {/* Mini equity curve */}
            <div className="st-equity-label">Equity Curve</div>
            <canvas className="st-equity-canvas" ref={el => {
              if (!el || !results.equityCurve.length) return;
              const ctx = el.getContext('2d');
              const w = el.parentElement.clientWidth - 16;
              const h = 100;
              const dpr = window.devicePixelRatio || 1;
              el.width = w * dpr; el.height = h * dpr;
              el.style.width = w + 'px'; el.style.height = h + 'px';
              ctx.scale(dpr, dpr);
              ctx.clearRect(0, 0, w, h);

              const data = results.equityCurve;
              const min = Math.min(...data.map(d => d.equity));
              const max = Math.max(...data.map(d => d.equity));
              const range = max - min || 1;
              const startVal = data[0].equity;

              // Zero line
              const zeroY = 4 + (h - 8) - ((startVal - min) / range) * (h - 8);
              ctx.strokeStyle = '#555'; ctx.lineWidth = 0.5; ctx.setLineDash([3, 3]);
              ctx.beginPath(); ctx.moveTo(0, zeroY); ctx.lineTo(w, zeroY); ctx.stroke();
              ctx.setLineDash([]);

              const last = data[data.length - 1].equity;
              ctx.strokeStyle = last >= startVal ? '#22c55e' : '#ef4444';
              ctx.lineWidth = 1.5; ctx.lineJoin = 'round';
              ctx.beginPath();
              for (let i = 0; i < data.length; i++) {
                const x = (i / (data.length - 1)) * w;
                const y = 4 + (h - 8) - ((data[i].equity - min) / range) * (h - 8);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
              }
              ctx.stroke();
            }} />

            {/* Trade list */}
            <div className="st-trades-label">Trades ({results.trades.length})</div>
            <div className="st-trades-list">
              {results.trades.slice(0, 50).map((t, i) => (
                <div key={i} className="st-trade-row">
                  <span className={`dir-badge dir-${t.direction === 'long' ? 'buy' : 'sell'}`}>
                    {t.direction.toUpperCase()}
                  </span>
                  <span className="st-trade-prices">{t.entryPrice.toFixed(2)} → {t.exitPrice.toFixed(2)}</span>
                  <span className={t.pnl >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                    {t.pnl >= 0 ? '+' : ''}{t.pnl}
                  </span>
                  <span className="st-trade-reason">{t.exitReason}</span>
                </div>
              ))}
              {results.trades.length > 50 && <div className="st-more">...and {results.trades.length - 50} more</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
